import React from 'react';

import { classnames } from '@/utils/classnames.ts';

export interface TagStyleProps {
    // `outlined` in a field, among what's typed; `subtle` for what's said aside, e.g. "du" after a name, which doesn't
    // take the name's weight; `negative` for what's overdue.
    tone?: 'muted' | 'outlined' | 'subtle' | 'negative';
    // `sm` after a name, on its line; `lg` for what's listed on its own, e.g. a recap's persons and files: as large as a
    // button, without a background until it's hovered or open, with the same room on both sides, so a row of them reads
    // evenly.
    size?: 'sm' | 'md' | 'lg';
    // A link or a button, e.g. a file that leads to its source: a step darker while hovered or open, and ringed while
    // focused.
    isPressable?: boolean;
}

// For a react-aria element that has to be the tag itself, e.g. a TagGroup's Tag, a link or a button.
export const tagClassName = ({ tone = 'muted', size = 'md', isPressable = false }: TagStyleProps) =>
    classnames(
        'inline-flex max-w-full shrink-0 items-center rounded-md whitespace-nowrap',
        size === 'sm' && 'h-5 gap-1.5 px-1.5 text-xs',
        size === 'md' && 'h-6 gap-1.5 px-2 text-xs',
        size === 'lg' && 'h-10 gap-2 rounded-xl px-3 text-base font-medium',
        tone === 'muted' && (size === 'lg' ? 'text-text' : 'bg-surface-muted text-text'),
        tone === 'outlined' && 'bg-surface ring-1 ring-border',
        tone === 'subtle' && 'bg-surface-muted font-normal text-text-muted',
        tone === 'negative' && 'bg-negative-surface text-negative',
        isPressable && [
            'cursor-default outline-none transition-colors data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary',
            tone === 'muted' &&
                (size === 'lg'
                    ? 'hovered:bg-surface-muted aria-expanded:bg-surface-muted'
                    : 'hovered:bg-surface-hover aria-expanded:bg-surface-hover'),
        ]
    );

export interface TagProps extends React.ComponentPropsWithRef<'span'>, Omit<TagStyleProps, 'isPressable'> {}

// A short value set apart from the text around it, e.g. a rule's condition, a due date or "du".
const Tag = ({ tone, size, className, ...props }: TagProps) => (
    <span {...props} className={classnames(tagClassName({ tone, size }), className)} />
);

export default Tag;
